// PRO Drift Auditor -- sortable institutional strategy comparison (TanStack v8).
//
// All 6 strategies are UNLOCKED. The AI Regime PRO row is highlighted (not
// blurred) because this is the paid tier. Columns are click-to-sort; the AI row
// is pinned visually with a gradient accent.

import React, { useMemo, useState } from 'react';
import {
    type SortingState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Sparkles, Table2 } from 'lucide-react';

import { Card } from './Card';
import { fmtMoney, fmtPct, signClass } from '../format';
import type { ProStrategyMetricsRow } from '../types';

const columnHelper = createColumnHelper<ProStrategyMetricsRow>();

const columns = [
    columnHelper.accessor('strategy_name', {
        header: 'Strategy',
        cell: (c) => {
            const isAi = c.row.original.strategy_id === 'ai_regime_pro';
            return (
                <span className={`inline-flex items-center gap-1.5 font-semibold ${isAi ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-800 dark:text-slate-100'}`}>
                    {isAi && <Sparkles className="h-3.5 w-3.5 text-indigo-500" />}
                    {c.getValue()}
                </span>
            );
        },
        enableSorting: false,
    }),
    columnHelper.accessor('final_wealth', {
        header: 'Final Wealth',
        cell: (c) => <span className="font-bold tabular-nums text-slate-900 dark:text-white">{fmtMoney(c.getValue())}</span>,
    }),
    columnHelper.accessor('cagr_pct', {
        header: 'CAGR',
        cell: (c) => <span className="tabular-nums">{fmtPct(c.getValue())}</span>,
    }),
    columnHelper.accessor('annualized_volatility_pct', {
        header: 'Vol',
        cell: (c) => <span className="tabular-nums">{fmtPct(c.getValue())}</span>,
    }),
    columnHelper.accessor('max_drawdown_pct', {
        header: 'Max DD',
        cell: (c) => <span className="tabular-nums text-rose-600 dark:text-rose-400 font-medium">{fmtPct(c.getValue())}</span>,
    }),
    columnHelper.accessor('sharpe_ratio', {
        header: 'Sharpe',
        cell: (c) => <span className="tabular-nums font-semibold">{c.getValue().toFixed(2)}</span>,
    }),
    columnHelper.accessor('sortino_ratio', {
        header: 'Sortino',
        cell: (c) => <span className="tabular-nums">{c.getValue().toFixed(2)}</span>,
    }),
    columnHelper.accessor('calmar_ratio', {
        header: 'Calmar',
        cell: (c) => <span className="tabular-nums">{c.getValue().toFixed(2)}</span>,
    }),
    columnHelper.accessor('cvar_95_pct', {
        header: 'CVaR 95%',
        cell: (c) => <span className="tabular-nums">{fmtPct(c.getValue())}</span>,
    }),
    columnHelper.accessor('variance_drain_total_dollars', {
        header: 'Variance Drain',
        cell: (c) => <span className="tabular-nums text-amber-600 dark:text-amber-400">{fmtMoney(c.getValue())}</span>,
    }),
    columnHelper.accessor('annual_turnover_pct', {
        header: 'Turnover',
        cell: (c) => <span className="tabular-nums">{fmtPct(c.getValue(), 1)}</span>,
    }),
    columnHelper.accessor((r) => r.total_fee_drag_dollars + r.total_tax_drag_dollars, {
        id: 'friction',
        header: 'Fee+Tax Drag',
        cell: (c) => <span className={`tabular-nums ${signClass(-c.getValue())}`}>{fmtMoney(c.getValue())}</span>,
    }),
    columnHelper.accessor('rebalance_events_count', {
        header: 'Rebalances',
        cell: (c) => <span className="tabular-nums">{c.getValue()}</span>,
    }),
];

export const StrategyComparisonTable: React.FC<{ rows: ProStrategyMetricsRow[] }> = ({ rows }) => {
    const data = useMemo(() => rows, [rows]);
    const [sorting, setSorting] = useState<SortingState>([{ id: 'final_wealth', desc: true }]);
    const table = useReactTable({
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <Card
            title="Strategy Comparison"
            subtitle="Same book, six rebalancing disciplines — net of transaction costs and realised capital-gains tax. Click any metric to sort."
            icon={<Table2 className="h-4 w-4" />}
            bodyClassName=""
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id} className="bg-slate-50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-800">
                                {hg.headers.map((header) => {
                                    const sortDir = header.column.getIsSorted();
                                    const canSort = header.column.getCanSort();
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className={`px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap ${canSort ? 'cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200' : ''}`}
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {canSort &&
                                                    (sortDir === 'asc' ? (
                                                        <ArrowUp className="h-3 w-3" />
                                                    ) : sortDir === 'desc' ? (
                                                        <ArrowDown className="h-3 w-3" />
                                                    ) : (
                                                        <ChevronsUpDown className="h-3 w-3 opacity-40" />
                                                    ))}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            const isAi = row.original.strategy_id === 'ai_regime_pro';
                            return (
                                <tr
                                    key={row.id}
                                    className={`border-b border-slate-50 dark:border-slate-800/60 last:border-0 ${
                                        isAi
                                            ? 'bg-gradient-to-r from-indigo-50/80 to-violet-50/40 dark:from-indigo-500/10 dark:to-violet-500/5'
                                            : 'hover:bg-slate-50/60 dark:hover:bg-slate-800/40'
                                    }`}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-3.5 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
